import { useState } from "react";
import { ColoredOutlineButton, OUTLINE_ICON_BUTTON_ICON_CLASS } from "@/components/ui/colored-outline-button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Truck } from "lucide-react";
import DeliveryInfo from "./DeliveryInfo";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTheme } from "@/context/ThemeContext";
import { useLanguage } from "@/context/LanguageContext";
import { carDetailsLabels } from "@/i18n";

interface DeliveryFeeZones {
  zoneA: number;
  zoneB: number;
  zoneC: number;
  zoneD: number;
}

interface DeliveryInfoButtonProps {
  deliveryFee?: DeliveryFeeZones;
  location?: string;
}

const DeliveryInfoButton = ({
  deliveryFee,
  location = "",
}: DeliveryInfoButtonProps) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const isMobile = useIsMobile();
  const { theme } = useTheme();
  const { language } = useLanguage();
  const deliveryLabel = carDetailsLabels(language).bidding.deliveryInfo;
  const isDark = theme === "dark";

  return (
    <>
      {isMobile ? (
        <>
          <ColoredOutlineButton
            type="button"
            size="icon"
            variant="neutral"
            onClick={() => setSheetOpen(true)}
          >
            <Truck className={OUTLINE_ICON_BUTTON_ICON_CLASS} strokeWidth={2.35} />
          </ColoredOutlineButton>
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetContent
              side="bottom"
              nestedSheet
              overlayClassName="!z-[230]"
              className={`${
                isDark
                  ? "bg-gray-900/80 backdrop-blur-md text-white border-gray-700"
                  : "bg-white/90 backdrop-blur-md text-black border-gray-200"
              } !z-[231] rounded-t-xl`}
            >
              <SheetHeader>
                <SheetTitle
                  className={`${isDark ? "text-white" : "text-black"} text-center`}
                >
                  {deliveryLabel}
                </SheetTitle>
              </SheetHeader>
              <div className="py-4 overflow-y-auto max-h-[70dvh]">
                <DeliveryInfo location={location} deliveryFee={deliveryFee} />
              </div>
            </SheetContent>
          </Sheet>
        </>
      ) : (
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
          <PopoverTrigger asChild>
            <div>
              <ColoredOutlineButton
                type="button"
                size="icon"
                variant="neutral"
              >
                <Truck className={OUTLINE_ICON_BUTTON_ICON_CLASS} strokeWidth={2.35} />
              </ColoredOutlineButton>
            </div>
          </PopoverTrigger>
          <PopoverContent
            className={`z-[220] w-72 p-4 ${
              isDark
                ? "bg-gray-900 text-white border-gray-700"
                : "bg-white text-black border-gray-200"
            } rounded-xl`}
            align="start"
          >
            <h3
              className={`font-medium mb-3 ${
                isDark ? "text-white" : "text-gray-800"
              }`}
            >
              {deliveryLabel}
            </h3>
            <DeliveryInfo location={location} deliveryFee={deliveryFee} />
          </PopoverContent>
        </Popover>
      )}
    </>
  );
};

export default DeliveryInfoButton;
